'use client'

import { motion, useReducedMotion } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { siteConfig } from '@/config/site'

// ============================================================================
// HERO — NovaVerse landing centerpiece.
// Displays the brand, tagline, creator credit and the single primary CTA.
// ============================================================================

export function Hero(): React.JSX.Element {
  const reduceMotion = useReducedMotion()

  const container = {
    hidden: {},
    visible: {
      transition: { staggerChildren: reduceMotion ? 0 : 0.12, delayChildren: 0.05 },
    },
  }

  const item = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const },
    },
  }

  return (
    <section className="relative flex min-h-[100svh] flex-col items-center justify-center px-4 pt-24 pb-16 sm:px-6">
      <motion.div
        variants={container}
        initial="hidden"
        animate="visible"
        className="mx-auto flex w-full max-w-3xl flex-col items-center text-center"
      >
        {/* Eyebrow badge */}
        <motion.div variants={item}>
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface/60 px-4 py-1.5 text-xs font-medium tracking-wide text-foreground/70 backdrop-blur">
            <span className="h-2 w-2 rounded-full bg-aqua-400 animate-pulse-glow" />
            Interactive Science Learning Universe
          </span>
        </motion.div>

        {/* Brand name */}
        <motion.h1
          variants={item}
          className="mt-8 text-5xl font-extrabold tracking-tight sm:text-7xl md:text-8xl"
        >
          <span className="text-gradient animate-gradient-pan">NOVAVERSE</span>
        </motion.h1>

        {/* Tagline */}
        <motion.p
          variants={item}
          className="mt-6 max-w-2xl text-balance text-lg font-medium text-foreground/80 sm:text-2xl"
        >
          &ldquo;{siteConfig.tagline}&rdquo;
        </motion.p>

        {/* Supporting copy */}
        <motion.p
          variants={item}
          className="mt-5 max-w-xl text-sm leading-relaxed text-muted sm:text-base"
        >
          A modern educational universe where concepts come alive through visual,
          hands-on and curiosity-driven learning.
        </motion.p>

        {/* Primary CTA (the only functional action in this step) */}
        <motion.div variants={item} className="mt-10 w-full sm:w-auto">
          <Button
            href={siteConfig.primaryCta.href}
            size="lg"
            className="w-full sm:w-auto"
          >
            {siteConfig.primaryCta.label}
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Button>
        </motion.div>

        {/* Creator credit */}
        <motion.p
          variants={item}
          className="mt-10 text-sm text-muted"
        >
          Created by{' '}
          <span className="font-semibold text-foreground/90">{siteConfig.creator}</span>
        </motion.p>
      </motion.div>
    </section>
  )
}
